<template>
  <div
    class="ll-data-table-pagination flex items-center justify-between gap-4"
    :class="{ 'll-data-table-pagination--sticky': sticky }"
  >
    <div class="flex items-center gap-2 text-sm text-gray-600">
      Items per page
      <LlSelect
        :model-value="pageSize"
        :items="pageSizeOptions"
        :return-object="false"
        density="compact"
        hide-details
        class="w-24"
        @update:model-value="$emit('update:pageSize', $event)"
      />
      <span>{{ rangeLabel }}</span>
    </div>
    <v-pagination
      :model-value="page"
      :length="totalPages || 1"
      :total-visible="5"
      density="compact"
      hide-details
      @update:model-value="$emit('update:page', $event)"
    />
  </div>
</template>

<script setup lang="ts">
// Reusable pagination footer shared across list pages (see LlDataTable for
// context). Placement varies per page today - Fixed Fees/Time Entries put
// this in a top action bar, Matters pins it to the bottom - so this stays a
// separate component the consuming page positions itself, with `sticky`
// only turning on the bottom-pinned treatment Matters needs.
import { computed } from 'vue';
import LlSelect from '@/components/LlSelect.vue';

const props = defineProps({
  page: { type: Number, required: true },
  pageSize: { type: Number, required: true },
  totalCount: { type: Number, default: 0 },
  pageSizeOptions: { type: Array, default: () => [10, 20, 50] },
  sticky: { type: Boolean, default: false },
});

defineEmits(['update:page', 'update:pageSize']);

const totalPages = computed(() => Math.ceil(props.totalCount / props.pageSize));

const rangeLabel = computed(() => {
  if (!props.totalCount) return '0 of 0';
  const start = (props.page - 1) * props.pageSize + 1;
  const end = Math.min(props.page * props.pageSize, props.totalCount);
  return `${start}–${end} of ${props.totalCount}`;
});
</script>

<style scoped>
/* The app shell's actual scrolling viewport is .leanlaw-main (see
   src/main/directives/views/main.html + src/css/unify-style.css), above
   wherever this component mounts - NOT anything in the consuming page's own
   markup. That page's wrapper divs must not set their own overflow
   (hidden/auto/scroll all count as scroll containers per spec, even with no
   visible scrollbar), or they'll "trap" this sticky positioning at a level
   that never actually scrolls. The 100px right padding reserves space for
   the help-desk chat bubble (~80x80px, bottom-right) so pagination controls
   never render underneath it. */
.ll-data-table-pagination--sticky {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 12px 100px 12px 4px;
  border-top: 1px solid #e5e7eb;
  z-index: 2;
}
</style>
